package com.evoapps.evoshift.domain.budget

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.NotFoundException
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase
import com.evoapps.evoshift.domain.usecase.auth.CallerCapabilities
import com.evoapps.evoshift.domain.usecase.auth.allows
import com.evoapps.evoshift.domain.usecase.finance.RegenerateDailyRecordsUseCase
import com.evoapps.evoshift.domain.util.calculateDurationHours
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import com.evoapps.evoshift.domain.model.RosterStatus
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.todayIn

/*
 * SH-147: people working without pay terms. A person with shifts but no pay terms at the
 * workplace costs nothing in every labour cost figure (their shifts get no cost snapshot), so a
 * month where 5 of 7 people had none showed a third of its real cost, without a word. Every
 * screen that shows labour cost now says who is missing, from the same rule:
 * - someone who works a shift in the period (assigned, or recorded as having worked it; shifts
 *   marked not worked do not count) and has no pay terms for the workplace;
 * - not the owner: working without a wage is normal for them (they may set pay terms to have
 *   their cost counted, and the workplace may mark their shifts unpaid);
 * - not someone confirmed as working without pay (WorkplacePayrollSettings.unpaidUserIds, e.g. a
 *   partner), whose shifts are not paid work at all;
 * - SH-206: never someone who does not count in the labour figures (LabourInclusion): their
 *   missing pay leaves no figure understated (a co-owner without pay terms, by default).
 */

/** One person working without pay terms in the period. */
data class PersonWithoutPay(
    val userId: String,
    /** Their name; PersonNames.UNNAMED when nobody could name them (the screens show a label). */
    val name: String,
    /** Their shifts in the period (paid work, not marked not worked). */
    val shifts: Int,
    /** Their planned hours in the period. */
    val hours: Double,
    /**
     * They hold the OWNER permission at the workplace (a co-owner) but are not its owner: likely
     * to work without a wage, so the confirmation is offered first.
     */
    val isCoOwner: Boolean = false
)

/** Who works without pay terms in a period ([people] sorted by name; empty: nothing to warn about). */
data class MissingPay(val workplaceId: String, val people: List<PersonWithoutPay> = emptyList()) {
    val isEmpty: Boolean get() = people.isEmpty()
    val count: Int get() = people.size
    val userIds: Set<String> get() = people.mapTo(mutableSetOf()) { it.userId }
}

object MissingPayRule {
    /**
     * The people among the workers of [shifts] who leave the labour cost understated: paid work
     * the owner's not ([Workplace.expectsPayTerms]) and [hasPayTerms] false. [hasPayTerms] null
     * means unknown (their terms could not be read): not counted, so a failed read never cries wolf.
     */
    fun peopleWithoutPay(workplace: Workplace, shifts: List<Shift>, hasPayTerms: (String) -> Boolean?): List<String> =
        workers(shifts).filter { uid -> workplace.expectsPayTerms(uid) && hasPayTerms(uid) == false }

    /** Everyone who works [shifts] (the recorded worker, else the assignee), shifts not worked aside. */
    fun workers(shifts: List<Shift>): List<String> =
        shifts.filterNot { it.notWorked }.mapNotNull { it.workerId }.distinct()
}

/**
 * SH-147: who works without pay terms at the roster's workplace in its month, or on one [date].
 * Money roles only (VIEW_FINANCES, who may also read pay terms): anyone else gets an empty answer
 * and nothing is read.
 */
class GetMissingPayUseCase(
    private val shiftRepository: ShiftRepository,
    private val userRepository: UserRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val caller: CallerCapabilities? = null,
    /** SH-206: people who do not count in the labour figures are never named. */
    private val labourInclusion: LabourInclusionReader? = null
) {
    suspend operator fun invoke(rosterId: String, date: String? = null): Result<MissingPay> = runCatching {
        val workplaceId = RosterIds.workplaceIdOf(rosterId)
        if (!caller.allows(workplaceId, Capability.VIEW_FINANCES)) return@runCatching MissingPay(workplaceId)
        coroutineScope {
            val workplace = async { workplaceRepository.getWorkplace(workplaceId).getOrThrow() }
            val shifts = async { shiftRepository.getShiftsForRoster(rosterId).getOrThrow() }
            val team = async { userRepository.getAssignableEmployees(workplaceId).getOrDefault(emptyList()) }
            val inPeriod = shifts.await().filter { date == null || it.date == date }
            val (year, month) = RosterIds.yearMonthOf(rosterId)
            of(workplace.await(), inPeriod, team.await().associateBy { it.uid }, LabourInclusion.monthOf(year, month))
        }
    }

    private suspend fun of(
        workplace: Workplace,
        shifts: List<Shift>,
        team: Map<String, com.evoapps.evoshift.domain.model.User>,
        month: String
    ): MissingPay = coroutineScope {
        val workers = MissingPayRule.workers(shifts).filter(workplace::expectsPayTerms)
        // SH-206: who does not count leaves no figure understated: not named (unknown settings: all named).
        val excluded = labourInclusion?.people(workplace, workers, month)?.getOrNull()?.excluded.orEmpty()
        val candidates = workers.filterNot { it in excluded }
        val terms = candidates.map { uid -> async { uid to hasPayTerms(uid, workplace.id) } }.awaitAll().toMap()
        val missing = MissingPayRule.peopleWithoutPay(workplace, shifts) { terms[it] }.filterNot { it in excluded }
        val people = missing.map { uid ->
            async {
                val theirs = shifts.filter { !it.notWorked && it.workerId == uid }
                val user = team[uid] ?: userRepository.getUserProfile(uid).getOrNull()
                PersonWithoutPay(
                    userId = uid,
                    // SH-237: nobody could name them: the banner shows its label, never the id.
                    name = user?.displayName?.takeIf { it.isNotBlank() } ?: com.evoapps.evoshift.domain.usecase.user.PersonNames.UNNAMED,
                    shifts = theirs.size,
                    hours = theirs.sumOf { calculateDurationHours(it.plannedTime.start, it.plannedTime.end) },
                    isCoOwner = user?.engagements?.get(workplace.id)?.permission == UserPermission.OWNER
                )
            }
        }.awaitAll().sortedBy { it.name.lowercase() }
        MissingPay(workplace.id, people)
    }

    /** True / false when known; null when the terms could not be read (not counted). */
    private suspend fun hasPayTerms(userId: String, workplaceId: String): Boolean? {
        val sensitive = userRepository.getSensitiveUserData(userId)
        return sensitive.fold(
            onSuccess = { it.financialEngagements[workplaceId] != null },
            // No private document at all: a person added without pay terms.
            onFailure = { e -> if (e is NotFoundException) false else null }
        )
    }
}

/**
 * SH-147: tells the screens showing labour cost that someone's pay terms (or their "works without
 * pay" confirmation) changed, so their figures and warnings are read again.
 */
class PayTermsChanges {
    private val _version = MutableStateFlow(0)

    /** Goes up by one on every change; screens react to the values after the one they started with. */
    val version: StateFlow<Int> = _version.asStateFlow()

    fun changed() = _version.update { it + 1 }
}

/**
 * SH-147: confirms (or undoes) that [userId] works at a workplace without pay (0 zł), stored in
 * its settings (owners and financial managers, as firestore.rules allow). SH-232: from the first
 * day of the current month on, as a period on the employee timeline ([UnpaidWork.withUnpaid]);
 * earlier months keep what they had. Their shifts from then on are not paid work: the months
 * still open from this one on are priced again (their cost snapshots go, or come back on an
 * undo) and their daily records and totals follow.
 */
class SetWorksUnpaidUseCase(
    private val workplaceRepository: WorkplaceRepository,
    private val rosterRepository: RosterRepository? = null,
    private val calculateShiftCost: CalculateShiftCostUseCase? = null,
    private val regenerateDailyRecords: RegenerateDailyRecordsUseCase? = null,
    private val changes: PayTermsChanges? = null,
    private val caller: CallerCapabilities? = null,
    /** Null: the workplace's own today (its time zone). */
    private val today: (() -> LocalDate)? = null
) {
    private fun todayAt(workplace: Workplace): LocalDate = today?.invoke() ?: run {
        val zone = runCatching { TimeZone.of(workplace.settings.timezone) }.getOrElse { TimeZone.currentSystemDefault() }
        AppClock.todayIn(zone)
    }

    suspend operator fun invoke(workplaceId: String, userId: String, unpaid: Boolean): Result<Unit> = runCatching {
        if (!caller.allows(workplaceId, Capability.EDIT_WORKPLACE_FINANCES)) {
            throw IllegalStateException("Only owners and financial managers change who works without pay")
        }
        val workplace = workplaceRepository.getWorkplace(workplaceId).getOrThrow()
        val payroll = workplace.settings.payroll
        val day = todayAt(workplace)
        // SH-232: from the first day of this month on (earlier months keep what they had).
        val before = UnpaidWork.periodsOf(payroll, userId)
        val after = UnpaidWork.withUnpaid(before, unpaid, UnpaidWork.monthStart(day))
        val periods = UnpaidWork.withPeriods(payroll, userId, after)
        val legacy = UnpaidWork.legacyOn(periods, emptyList(), day.toString())
        if (after != before || legacy != payroll.unpaidUserIds.sorted()) {
            workplaceRepository.updateUnpaidWork(workplaceId, periods, legacy, day.toString()).getOrThrow()
            reprice(workplaceId, day)
        }
        changes?.changed()
    }

    /**
     * The months still open, from this month on (older ones are history): their snapshots and
     * daily records follow the change. Best effort: the setting is saved either way, and opening a
     * month prices what is missing.
     */
    private suspend fun reprice(workplaceId: String, now: LocalDate) {
        val rosters = rosterRepository ?: return
        val pricing = calculateShiftCost ?: return
        // SH-232: the change applies from this month on: earlier months keep their snapshots.
        val firstMonth = now.year * 12 + now.month.number - 1
        rosters.observeRosters(workplaceId).first()
            .filter { it.status != RosterStatus.LOCKED && it.status != RosterStatus.ARCHIVED && it.year * 12 + it.month - 1 >= firstMonth }
            .forEach { roster ->
                runCatching {
                    pricing.refreshRoster(roster.id).getOrThrow()
                    regenerateDailyRecords?.invoke(workplaceId, roster.id, roster.year, roster.month)?.getOrThrow()
                }
            }
    }
}
